<script setup lang="ts">
withDefaults(defineProps<{
    headline?: string;
    subHead?: string;
}>(), {
    headline: '',
    subHead: '',
});

const ellipse87 = '/img/2026/gallery/ellipse-87.svg';
const ellipse88 = '/img/2026/gallery/ellipse-88.svg';

const pd01 = '/img/2026/partner-day-gallery/pd01.png';
const pd02 = '/img/2026/partner-day-gallery/pd02.png';
const pd03 = '/img/2026/partner-day-gallery/pd03.png';
const pd04 = '/img/2026/partner-day-gallery/pd04.png';
const pd05 = '/img/2026/partner-day-gallery/pd05.png';
const pd06 = '/img/2026/partner-day-gallery/pd06.png';
const pd07 = '/img/2026/partner-day-gallery/pd07.png';
const pd08 = '/img/2026/partner-day-gallery/pd08.png';
const pd09 = '/img/2026/partner-day-gallery/pd09.png';
const pd10 = '/img/2026/partner-day-gallery/pd10.png';
const pd11 = '/img/2026/partner-day-gallery/pd11.png';
const pd12 = '/img/2026/partner-day-gallery/pd12.png';
const pd13 = '/img/2026/partner-day-gallery/pd13.png';
const pd14 = '/img/2026/partner-day-gallery/pd14.png';
const pd15 = '/img/2026/partner-day-gallery/pd15.png';
const pd16 = '/img/2026/partner-day-gallery/pd16.png';
const pd17 = '/img/2026/partner-day-gallery/pd17.png';
const pd18 = '/img/2026/partner-day-gallery/pd18.png';
const pd19 = '/img/2026/partner-day-gallery/pd19.png';
const pd20 = '/img/2026/partner-day-gallery/pd20.png';
const pd21 = '/img/2026/partner-day-gallery/pd21.png';
const pd22 = '/img/2026/partner-day-gallery/pd22.png';
const pd23 = '/img/2026/partner-day-gallery/pd23.png';
const pd24 = '/img/2026/partner-day-gallery/pd24.png';
const pd25 = '/img/2026/partner-day-gallery/pd25.png';
const pd26 = '/img/2026/partner-day-gallery/pd26.png';
const pd27 = '/img/2026/partner-day-gallery/pd27.png';
const pd28 = '/img/2026/partner-day-gallery/pd28.png';
const pd29 = '/img/2026/partner-day-gallery/pd29.png';
const pd30 = '/img/2026/partner-day-gallery/pd30.png';
const pd31 = '/img/2026/partner-day-gallery/pd31.png';
const pd32 = '/img/2026/partner-day-gallery/pd32.png';
const pd33 = '/img/2026/partner-day-gallery/pd33.png';

const sectionRef = ref(null);
const canvasRef = ref(null);
const scale = ref(1);

const hoveredPhoto = ref(-1);

// Drag-to-pan offset
const panOffset = reactive({ x: 0, y: 0 });
const isDragging = ref(false);
const dragStart = { x: 0, y: 0, ox: 0, oy: 0 };

const DESIGN_W = 1440;
const DESIGN_H = 751;
const OVERAGE = 1;

// Bounds based on actual photo edges
const CONTENT_LEFT = -131;
const CONTENT_RIGHT = 3748;
const CONTENT_TOP = -333;
const CONTENT_BOTTOM = 828;

const dragBounds = reactive({ minX: 0, maxX: 0, minY: 0, maxY: 0 });

function clamp(val, min, max) {
    return Math.min(max, Math.max(min, val));
}

function rubberBand(val, min, max) {
    if (val < min) return min - (min - val) * 0.3;
    if (val > max) return max + (val - max) * 0.3;
    return val;
}

function updateScale() {
    if (!sectionRef.value) return;
    const w = sectionRef.value.clientWidth;
    const h = sectionRef.value.clientHeight;
    const s = Math.min(1, Math.max(w / DESIGN_W, h / DESIGN_H) * OVERAGE);
    scale.value = s;

    const cx = DESIGN_W / 2;
    const cy = DESIGN_H / 2;
    dragBounds.minX = -Math.max(0, (CONTENT_RIGHT - cx) * s - w / 2);
    dragBounds.maxX = Math.max(0, (cx - CONTENT_LEFT) * s - w / 2);
    dragBounds.minY = -Math.max(0, (CONTENT_BOTTOM - cy) * s - h / 2);
    dragBounds.maxY = Math.max(0, (cy - CONTENT_TOP) * s - h / 2);

    panOffset.x = clamp(panOffset.x, dragBounds.minX, dragBounds.maxX);
    panOffset.y = clamp(panOffset.y, dragBounds.minY, dragBounds.maxY);
}

// Velocity tracking for fling
const velocity = { x: 0, y: 0 };
let lastPointer = { x: 0, y: 0, t: 0 };
let flingRaf = null;

// Touch direction detection
const gestureDir = ref('pending');
const GESTURE_THRESHOLD = 8;

function onPointerDown(e) {
    gestureDir.value = e.pointerType === 'touch' ? 'pending' : 'pan';
    isDragging.value = true;
    dragStart.x = e.clientX;
    dragStart.y = e.clientY;
    dragStart.ox = panOffset.x;
    dragStart.oy = panOffset.y;
    lastPointer.x = e.clientX;
    lastPointer.y = e.clientY;
    lastPointer.t = performance.now();
    velocity.x = 0;
    velocity.y = 0;
    if (flingRaf) cancelAnimationFrame(flingRaf);
    if (e.pointerType !== 'touch') {
        canvasRef.value?.setPointerCapture(e.pointerId);
    }
}

function onPointerMove(e) {
    if (!isDragging.value) return;

    if (gestureDir.value === 'pending') {
        const dx = Math.abs(e.clientX - dragStart.x);
        const dy = Math.abs(e.clientY - dragStart.y);
        if (dx < GESTURE_THRESHOLD && dy < GESTURE_THRESHOLD) return;
        if (dx >= dy) {
            gestureDir.value = 'pan';
            canvasRef.value?.setPointerCapture(e.pointerId);
        } else {
            gestureDir.value = 'scroll';
            isDragging.value = false;
            return;
        }
    }

    const now = performance.now();
    const dt = now - lastPointer.t;
    if (dt > 0) {
        velocity.x = (e.clientX - lastPointer.x) / dt;
        velocity.y = (e.clientY - lastPointer.y) / dt;
    }
    lastPointer.x = e.clientX;
    lastPointer.y = e.clientY;
    lastPointer.t = now;
    const rawX = dragStart.ox + (e.clientX - dragStart.x);
    const rawY = dragStart.oy + (e.clientY - dragStart.y);
    panOffset.x = rubberBand(rawX, dragBounds.minX, dragBounds.maxX);
    panOffset.y = rubberBand(rawY, dragBounds.minY, dragBounds.maxY);
}

function onPointerUp() {
    isDragging.value = false;
    const FRICTION = 0.95;
    const MIN_V = 0.05;
    const OVERSHOOT_MAX = 80;
    const BOUNCE_STIFFNESS = 0.12;
    let vx = velocity.x * 16;
    let vy = velocity.y * 16;

    function step() {
        vx *= FRICTION;
        vy *= FRICTION;

        if (panOffset.x < dragBounds.minX) {
            vx += (dragBounds.minX - panOffset.x) * BOUNCE_STIFFNESS;
        } else if (panOffset.x > dragBounds.maxX) {
            vx += (dragBounds.maxX - panOffset.x) * BOUNCE_STIFFNESS;
        }
        if (panOffset.y < dragBounds.minY) {
            vy += (dragBounds.minY - panOffset.y) * BOUNCE_STIFFNESS;
        } else if (panOffset.y > dragBounds.maxY) {
            vy += (dragBounds.maxY - panOffset.y) * BOUNCE_STIFFNESS;
        }

        panOffset.x = clamp(panOffset.x + vx, dragBounds.minX - OVERSHOOT_MAX, dragBounds.maxX + OVERSHOOT_MAX);
        panOffset.y = clamp(panOffset.y + vy, dragBounds.minY - OVERSHOOT_MAX, dragBounds.maxY + OVERSHOOT_MAX);

        const inBounds = panOffset.x >= dragBounds.minX && panOffset.x <= dragBounds.maxX
            && panOffset.y >= dragBounds.minY && panOffset.y <= dragBounds.maxY;
        if (Math.abs(vx) < MIN_V && Math.abs(vy) < MIN_V && inBounds) {
            panOffset.x = clamp(panOffset.x, dragBounds.minX, dragBounds.maxX);
            panOffset.y = clamp(panOffset.y, dragBounds.minY, dragBounds.maxY);
            return;
        }
        flingRaf = requestAnimationFrame(step);
    }
    flingRaf = requestAnimationFrame(step);
}

function onWheel(e) {
    if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
    if (e.deltaX === 0) return;
    e.preventDefault();
    if (flingRaf) cancelAnimationFrame(flingRaf);
    panOffset.x = clamp(panOffset.x - e.deltaX, dragBounds.minX, dragBounds.maxX);
}

const isVisible = ref(false);
const prefersReducedMotion = ref(false);

onMounted(() => {
    updateScale();
    window.addEventListener('resize', updateScale);
    sectionRef.value?.addEventListener('wheel', onWheel, { passive: false });

    prefersReducedMotion.value = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion.value) {
        isVisible.value = true;
    } else {
        const isMobile = window.innerWidth < 768;
        const obs = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                isVisible.value = true;
                obs.disconnect();
            }
        }, { threshold: isMobile ? 0.2 : 0.75 });
        if (sectionRef.value) obs.observe(sectionRef.value);
    }
});

onUnmounted(() => {
    window.removeEventListener('resize', updateScale);
    sectionRef.value?.removeEventListener('wheel', onWheel);
    if (flingRaf) cancelAnimationFrame(flingRaf);
});

const canvasStyle = computed(() => ({
    width: DESIGN_W + 'px',
    height: DESIGN_H + 'px',
    transform: `translate(calc(-50% + ${panOffset.x}px), calc(-50% + ${panOffset.y}px)) scale(${scale.value})`,
    cursor: isDragging.value ? 'grabbing' : 'grab',
}));

const ellipses = [
    { src: ellipse87, x: 640, y: 588, w: 1004, h: 1004, expand: '-29.88%' },
    { src: ellipse88, x: -367, y: -410, w: 785, h: 785, expand: '-25.48%' },
];

const photos = [
    { src: pd12, alt: 'Two men in business attire having a conversation at a conference', x: -103, y: -145, w: 467, h: 463.5, inset: '0.15% 1.39% 0.77% 0', depth: 0.4 },
    { src: pd06, alt: 'Three men networking and discussing documents at a modern conference venue', x: 306, y: -108, w: 259.5, h: 194, inset: '0.54% 0 0 4.44%', depth: 0.7 },
    { src: pd04, alt: 'Crowded conference space with many attendees networking and moving through the venue', x: 615, y: -266, w: 391, h: 541, inset: '2.4% 2.24% 2.58% 0', depth: 0.3 },
    { src: pd07, alt: 'Woman with brown hair and sunglasses smiling warmly at a networking event', x: 997.5, y: -332, w: 544.5, h: 544, inset: '1.28% 1.73% 0.46% 0.67%', depth: 0.6 },
    { src: pd02, alt: 'Man in glasses and patterned shirt sits next to woman at conference event', x: 268, y: 136, w: 297, h: 312.5, inset: '0 0 3.23% 3.14%', depth: 0.5 },
    { src: pd11, alt: 'Two business professionals smiling while looking at a laptop', x: 1335, y: 84.5, w: 295.5, h: 305, inset: '3.51% 1.81% 1.67% 4.03%', depth: 0.3 },
    { src: pd05, alt: 'Smiling man with conference badge networking with colleagues at outdoor event', x: 699, y: 193, w: 525.5, h: 428, inset: '0.21% 1.66% 1.18% 1.08%', depth: 0.6 },
    { src: pd03, alt: 'Woman with red lanyard presenting at conference booth with attendees in background', x: 462, y: 285, w: 271.5, h: 318.5, inset: '7.28% 2.41% 1.02% 3.87%', depth: 0.4 },
    { src: pd13, alt: 'Smiling woman with blonde hair and sunglasses at conference event wearing lanyard', x: -131, y: 306.13, w: 320, h: 274.366, inset: '1.61% 4.14% 0 1.76%', depth: 0.7 },
    { src: pd10, alt: 'Smiling blonde woman in dark top at conference event, wearing lanyard and holding drink', x: 1196, y: 356.5, w: 350, h: 410.5, inset: '1.21% 0.5% 0 0.86%', depth: 0.5 },
    { src: pd01, alt: 'Crowded networking event with attendees in business attire socializing and holding drinks', x: 128, y: 376, w: 492, h: 491, depth: 0.3 },
    { src: pd09, alt: 'Bearded man in button-up shirt with red lanyard speaking at conference event', x: 1089, y: 528, w: 284, h: 340, inset: '8.44% 5.71% 1.14% 0.84%', depth: 0.6 },
    { src: pd08, alt: 'Two women with glasses collaborating on a laptop at a conference', x: 640, y: 593, w: 431, h: 232, inset: '0.46% 1.84% 0 5.57%', depth: 0.4 },
    { src: pd14, alt: 'Man in dark jacket presenting at conference with laptop, gesturing while speaking', x: -105, y: 629, w: 319, h: 200, inset: '0 5.82% 0 0', depth: 0.7 },
    { src: pd17, alt: 'Business professionals having a discussion around a table in a modern office setting', x: 1490, y: -84, w: 324, h: 224, inset: '1.33% 7.79% 3.2% 2.35%', depth: 0.5 },
    { src: pd16, alt: 'Three people sitting and having a conversation at a conference or panel event', x: 1586, y: 54, w: 476, h: 403.5, inset: '2.08% 5.13% 5.09% 3.86%', depth: 0.4 },
    { src: pd15, alt: 'Professional woman in dark blazer and orange shirt presenting with gesturing hands', x: 1568.5, y: 442, w: 298.5, h: 358.5, inset: '1.29% 1.65% 0.92% 0.6%', depth: 0.6 },
    { src: pd18, alt: 'Two professionals having a business meeting at a conference', x: 1802, y: 304.5, w: 274.5, h: 313.5, inset: '4.03% 0 6.02% 6.67%', depth: 0.3 },
    { src: pd19, alt: 'Three smiling conference attendees with name badges toast with beer glasses', x: 1830, y: -190, w: 576.5, h: 520, inset: '0.57% 1.09% 2.43% 0.74%', depth: 0.5 },
    { src: pd29, alt: 'Two men smiling and talking at a conference, one wearing a lanyard', x: 1822, y: 599, w: 256.5, h: 176.5, inset: '10.29% 13.58% 0 5.52%', depth: 0.7 },
    { src: pd20, alt: 'Woman with glasses smiling while talking to man at networking event', x: 2030, y: 368, w: 458, h: 450, inset: '2.93% 2.77% 0.73% 1.05%', depth: 0.4 },
    { src: pd21, alt: 'Man with beard and sunglasses wearing conference lanyard and blue checkered shirt', x: 2271.5, y: 101, w: 197.5, h: 428.5, inset: '13.66% 2.89% 6.57% 3.71%', depth: 0.6 },
    { src: pd22, alt: 'Two men in business attire having a conversation at an outdoor conference', x: 2367, y: 558.5, w: 515, h: 256.5, inset: '1.93% 2.11% 0.3% 6.15%', depth: 0.3 },
    { src: pd23, alt: 'Woman with red lanyard smiling while talking to man in suit at conference', x: 2463.5, y: -21, w: 419.5, h: 584, inset: '0 1.52% 3.29% 0.64%', depth: 0.5 },
    { src: pd24, alt: 'Two professionals having a conversation at an outdoor conference', x: 2660, y: 367.5, w: 397, h: 262.5, inset: '2.98% 3.8% 1.05% 6.68%', depth: 0.7 },
    { src: pd30, alt: 'Professional woman with blonde hair in ponytail speaking at conference event', x: 2748, y: -184, w: 380, h: 262.5, inset: '1.44% 7.34% 2.46% 2.08%', depth: 0.4 },
    { src: pd26, alt: 'Group of professionals networking and conversing at an outdoor conference event', x: 2851.5, y: 25.5, w: 475.5, h: 376.5, inset: '0.49% 1.83% 2.49% 1.84%', depth: 0.6 },
    { src: pd31, alt: 'Three professionals sitting and smiling during a conference or business event', x: 2886.5, y: 544, w: 457, h: 319, inset: '0.63% 1.04% 1.05% 1.35%', depth: 0.3 },
    { src: pd25, alt: 'Man in blue shirt with lanyard smiling at conference event', x: 3013, y: 280, w: 466.5, h: 319, inset: '0.81% 2.21% 4.3% 8.13%', depth: 0.5 },
    { src: pd33, alt: 'Smiling woman with blonde hair in white patterned top at conference', x: 3135, y: -152, w: 258.5, h: 217.5, inset: '4.4% 2.34% 3.26% 6.29%', depth: 0.7 },
    { src: pd27, alt: 'Three diverse professionals networking and smiling at a conference event', x: 3326.5, y: -28, w: 397.5, h: 351, inset: '0 0.89% 0.73% 2.16%', depth: 0.4 },
    { src: pd32, alt: 'Woman with long brown hair speaking into microphone at conference with purple backdrop', x: 3355.5, y: 609, w: 381, h: 215, inset: '6.08% 1.49% 0 2.34%', depth: 0.6 },
    { src: pd28, alt: 'Professional woman in gray blazer smiling during conference presentation', x: 3471, y: 358, w: 277, h: 309.5, inset: '1.73% 0 1.86% 3.06%', depth: 0.5 },
];

function photoStyle(photo, i) {
    const hovered = hoveredPhoto.value === i;
    const anyHovered = hoveredPhoto.value !== -1;
    const s = hovered ? 1.05 : anyHovered ? 0.97 : 1;
    return {
        left: photo.x + 'px',
        top: photo.y + 'px',
        width: photo.w + 'px',
        height: photo.h + 'px',
        transform: `scale(${s})`,
    };
}
</script>

<template>
    <section ref="sectionRef"
        aria-label="Photo gallery from Partner Day events"
        class="block-pd-gallery relative w-full h-[80svh] md:h-[751px] overflow-hidden"
        style="background: linear-gradient(9.39deg, #FF9700 5.16%, #FF4800 20.66%, #820CA7 137.7%);"
        >

        <div ref="canvasRef"
            class="absolute left-1/2 top-1/2 select-none"
            style="touch-action: pan-y;"
            :style="canvasStyle"
            aria-hidden="true"
            @pointerdown="onPointerDown"
            @pointermove="onPointerMove"
            @pointerup="onPointerUp"
            @pointercancel="onPointerUp">

            <!-- Atmospheric blurred ellipses -->
            <div v-for="(el, i) in ellipses" :key="'e' + i"
                class="absolute"
                :style="{ left: el.x + 'px', top: el.y + 'px', width: el.w + 'px', height: el.h + 'px' }">
                <div class="absolute" :style="{ inset: el.expand }">
                    <img :src="el.src" loading="lazy" decoding="async" alt="" draggable="false" class="display-block max-w-none size-full" />
                </div>
            </div>

            <!-- Masked photos with parallax + pop-in -->
            <div v-for="(photo, i) in photos" :key="'p' + i"
                :class="['absolute transition-transform duration-700 ease-out', isVisible ? 'pd-gallery-pop-in' : 'pd-gallery-hidden']"
                :style="{ ...photoStyle(photo, i), animationDelay: (i * 60) + 'ms' }"
                @mouseenter="hoveredPhoto = i"
                @mouseleave="hoveredPhoto = -1">
                <div v-if="photo.inset" class="absolute" :style="{ inset: photo.inset }">
                    <img :src="photo.src" loading="lazy" decoding="async" :alt="photo.alt || ''" draggable="false" class="display-block max-w-none size-full" />
                </div>
                <img v-else :src="photo.src" loading="lazy" decoding="async" :alt="photo.alt || ''" draggable="false" class="absolute display-block max-w-none size-full" />
            </div>
        </div>
    </section>
</template>

<style>
.pd-gallery-hidden {
    scale: 0;
    opacity: 0;
}

.pd-gallery-pop-in {
    animation: pd-gallery-bounce-in 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

@keyframes pd-gallery-bounce-in {
    from {
        scale: 0;
        opacity: 0;
    }
    to {
        scale: 1;
        opacity: 1;
    }
}

@media (prefers-reduced-motion: reduce) {
    .pd-gallery-hidden {
        scale: 1;
        opacity: 1;
    }

    .pd-gallery-pop-in {
        animation: none;
        scale: 1;
        opacity: 1;
    }
}
</style>
